import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import type { CreateCustomSectionInput } from "@/hooks/use-custom-sections-structures";

const formSchema = z.object({
  section_token: z
    .string()
    .min(2, "Token must be at least 2 characters")
    .max(50, "Token must be at most 50 characters")
    .regex(
      /^[A-Z][A-Z0-9_]*$/,
      "Token must be UPPER_SNAKE_CASE (e.g., MY_CUSTOM_SECTION)"
    ),
  section_title: z
    .string()
    .min(2, "Title must be at least 2 characters")
    .max(100, "Title must be at most 100 characters"),
  default_text: z.string().optional(),
  scope: z.enum(["template", "user"]),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCustomSectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateCustomSectionInput) => Promise<unknown>;
  examFamilyId?: string;
}

export function CreateCustomSectionDialog({
  open,
  onOpenChange,
  onCreate,
  examFamilyId,
}: CreateCustomSectionDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      section_token: "",
      section_title: "",
      default_text: "",
      scope: examFamilyId ? "template" : "user",
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const result = await onCreate({
        section_token: values.section_token,
        section_title: values.section_title,
        default_text: values.default_text,
        exam_family_id: values.scope === "template" ? examFamilyId : undefined,
        scope: values.scope,
      });

      if (result) {
        form.reset();
        onOpenChange(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-generate token from title
  const handleTitleChange = (title: string) => {
    form.setValue("section_title", title);
    // Only auto-generate if token is empty or was auto-generated
    const currentToken = form.getValues("section_token");
    if (!currentToken || currentToken === generateToken(form.getValues("section_title"))) {
      form.setValue("section_token", generateToken(title));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Custom Section</DialogTitle>
          <DialogDescription>
            Create a new section that you can add to your report templates.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="section_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Section Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Additional Findings"
                      {...field}
                      onChange={(e) => handleTitleChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The display name shown in reports
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="section_token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Section Token</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., ADDITIONAL_FINDINGS"
                      className="font-mono"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Unique identifier in UPPER_SNAKE_CASE
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Text (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter default content for this section..."
                      className="min-h-[80px] font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Pre-filled text when this section is added
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {examFamilyId && (
                        <SelectItem value="template">
                          This template only
                        </SelectItem>
                      )}
                      <SelectItem value="user">
                        All my templates
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Where this custom section can be used
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Section
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Helper to generate token from title
function generateToken(title: string): string {
  return title
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 50);
}
